// src/api/index.ts
import { healthService } from "../services/ApiService";
import { userApiService } from "../services/UserApiService";
import { captureService } from "../services/CaptureService";
import { apiConfig } from "../config/apiConfig";
import { getLogger } from "../helpers/logger";
import type { StoryFlavor } from "../utils/storyFlavor";

const apiLogger = getLogger("API");

// Re-export types for backward compatibility
export * from "../services/UserApiService";
export * from "../services/CaptureService";

// Type definitions for API requests and responses
export interface CaptureWebsiteRequest {
  url?: string;
  urls?: string[];
  batch?: boolean;
  viewports: string[];
  themes: string[];
  userAgent?: string;
  /**
   * Capture only the element matching this CSS selector. Supported end to end by CaptureService
   * and the capture route; this copy of the interface had simply drifted out of step with them.
   */
  selector?: string;
}

export interface CaptureFromCodeRequest {
  html: string;
  css: string;
  viewports: string[];
  themes: string[];
}

export interface CaptureFromZipRequest {
  viewports: string[];
  themes: string[];
  customViewports?: Record<string, string>;
}


export interface CaptureResponse {
  url: string;
  /**
   * NEVER SENT by the worker. No route puts it in a response — `getUpdatedQuota` is imported by
   * two routes and called by none, and its free-tier limit defaults to 999999 regardless. Typed
   * optional so callers are forced to handle its absence, which is the only case that occurs.
   */
  isSuccess: boolean;
  executionTime: string;
  requestId: string;
}

export interface ZipCaptureResponse extends CaptureResponse {
  hostingSiteInfo?: {
    siteId: string;
  };
  captureInfo?: {
    viewports: string[];
    themes: string[];
    captureCount: number;
  };
}

export interface UserStatusResponse {
  isSuccess: boolean;
  membership: 'PRO' | 'FREE';
  rateLimit: {
    nextAvailableTime: string;
    cooldownRemaining?: number;
  } | null;
  blocked?: boolean;
  message?: string;
}

export interface CodeResponse {
  isSuccess: boolean;
  message?: string;
  reason?: string;
  code?: string;
  expiresAt?: number;
}

export interface HealthResponse {
  status: string;
  version: string;
  uptime: number;
}

// Modern API class that delegates to specialized services
export class Api {
  // Backward compatibility properties
  private cancelTokenSources: {
    capture?: AbortController;
    fetch?: AbortController;
  } = {};

  private pendingAbortControllers: AbortController[] = [];
  private connectionStatus: "online" | "offline" | "checking" = "checking";
  private connectionListeners: Array<
    (status: "online" | "offline" | "checking") => void
  > = [];
  private lastHealthCheckTime: number = 0;
  private healthCheckCacheMs: number = 1000 * 60 * 30; // 30 minutes
  private healthCheckPromise: Promise<HealthResponse | null> | null = null;

  constructor() {
    // Subscribe to health service status changes
    healthService.onConnectionStatusChange((status) => {
      this.connectionStatus = status;
      this.connectionListeners.forEach(listener => listener(status));
    });
  }

  // Add subscription for connection status
  onConnectionStatusChange(
    listener: (status: "online" | "offline" | "checking") => void,
  ): () => void {
    this.connectionListeners.push(listener);
    // Immediately inform about current status
    listener(this.connectionStatus);
    return () => {
      this.connectionListeners = this.connectionListeners.filter(
        (l) => l !== listener,
      );
    };
  }

  // Update the connection status and notify listeners
  private setConnectionStatus(status: "online" | "offline" | "checking"): void {
    if (this.connectionStatus !== status) {
      this.connectionStatus = status;
      this.connectionListeners.forEach((listener) => listener(status));
    }
  }

  // Cancel any ongoing requests - delegates to all services
  cancelRequests(): boolean {
    let cancelled = false;

    // Cancel using new services
    cancelled = healthService.cancelAllRequests() || cancelled;
    cancelled = userApiService.cancelAllRequests() || cancelled;
    cancelled = captureService.cancelAllRequests() || cancelled;

    // Legacy cancellation for backward compatibility
    if (this.cancelTokenSources.capture) {
      this.cancelTokenSources.capture.abort();
      this.cancelTokenSources.capture = undefined;
      cancelled = true;
    }

    if (this.cancelTokenSources.fetch) {
      this.cancelTokenSources.fetch.abort();
      this.cancelTokenSources.fetch = undefined;
      cancelled = true;
    }

    this.pendingAbortControllers.forEach((controller) => {
      try {
        controller.abort();
        cancelled = true;
      } catch (e) {
        apiLogger.error("Error aborting request:", e);
      }
    });

    this.pendingAbortControllers = [];
    return cancelled;
  }

  // Enhanced health check - delegates to health service
  async checkHealth(silent: boolean = false): Promise<HealthResponse | null> {
    try {
      const isHealthy = await healthService.checkHealth(silent);
      
      // For backward compatibility, return a HealthResponse-like object
      if (isHealthy) {
        return {
          status: "healthy",
          version: "1.0.0", // Default version
          uptime: Date.now(), // Mock uptime
        };
      } else {
        return null;
      }
    } catch (error) {
      if (!silent) {
        apiLogger.error("Health check failed:", error);
      }
      throw error;
    }
  }

  // Get user status - delegates to user API service
  async getUserStatus(userId: string, forceRefresh = false): Promise<UserStatusResponse> {
    return userApiService.getUserStatus(userId, forceRefresh);
  }

  // Check user status - delegates to user API service
  async checkUserStatus(userId: string): Promise<{
    canPerformAction: boolean;
    statusMessage: string | null;
    userStatus?: UserStatusResponse;
  }> {
    const result = await userApiService.checkUserStatus(userId);
    return {
      canPerformAction: result.canPerformAction,
      statusMessage: result.statusMessage,
      userStatus: result.userStatus,
    };
  }

  // Discover a site's pages (sitemap.xml, else same-origin link crawl) for the
  // multi-page import flow.

  async discoverPages(
    url: string,
    figmaUID: string,
    figmaUserName?: string,
  ): Promise<{ isSuccess: boolean; urls: string[]; count: number; source: string; message?: string }> {
    const qs = new URLSearchParams({
      figmaUID: figmaUID || "anonymous",
      figmaUserName: figmaUserName || "Anonymous User",
    });
    try {
      const response = await fetch(`${apiConfig.baseUrl}/sitemap?${qs.toString()}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });
      const data = await response.json();
      return {
        isSuccess: !!data.isSuccess,
        urls: Array.isArray(data.urls) ? data.urls : [],
        count: typeof data.count === "number" ? data.count : 0,
        source: data.source || "none",
        message: data.message,
      };
    } catch (error) {
      return {
        isSuccess: false,
        urls: [],
        count: 0,
        source: "none",
        message: error instanceof Error ? error.message : "Discovery failed",
      };
    }
  }

  // Discover a Storybook's or Histoire's stories for the Storybook tab. Server-side because the
  // plugin UI can only reach the domains the manifest allows, and because that puts the URL
  // behind the same SSRF check every other capture target goes through.
  async discoverStorybook(
    url: string,
    figmaUID: string,
    figmaUserName?: string,
  ): Promise<{
    isSuccess: boolean;
    /** Which runner answered. The capture selector differs, so the caller has to know. */
    flavor?: StoryFlavor;
    stories: Array<{ id: string; title: string; name: string; url: string }>;
    count: number;
    total?: number;
    version?: number | null;
    docsSkipped?: number;
    truncated?: boolean;
    message?: string;
  }> {
    try {
      // The identity MUST travel. Without it authAndQuotaMiddleware falls back to the shared
      // `anonymous` user, so every discovery in the world shares one abuse counter — 51 clicks an
      // hour across all users disables the tab for everyone — and a PRO user's quota bypass never
      // applies, because `anonymous` resolves to FREE.
      const qs = new URLSearchParams({
        figmaUID: figmaUID || 'anonymous',
        figmaUserName: figmaUserName || 'Anonymous User',
      });
      const response = await fetch(`${apiConfig.baseUrl}/storybook?${qs.toString()}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url }),
      });
      const data = await response.json();
      return {
        isSuccess: !!data.isSuccess,
        flavor: data.flavor === 'histoire' ? 'histoire' : 'storybook',
        stories: Array.isArray(data.stories) ? data.stories : [],
        count: typeof data.count === "number" ? data.count : 0,
        total: data.total,
        version: data.version,
        docsSkipped: data.docsSkipped,
        truncated: !!data.truncated,
        message: data.message,
      };
    } catch (error) {
      return {
        isSuccess: false,
        stories: [],
        count: 0,
        message: error instanceof Error ? error.message : "Discovery failed",
      };
    }
  }

  /**
   * Fetch, with the request registered so `cancelRequests` can abort it.
   *
   * There used to be a CORS-proxy path here: a `proxyEnabled` flag, a remembered
   * last-successful proxy, and a loop over `API_CONFIG.corsProxies`. None of it could run.
   * `corsProxies` is `[]`, so the loop body never executed, `lastError` stayed null, and the
   * function then SLEPT through the full retry schedule doing nothing before throwing "All CORS
   * proxies failed" — with zero proxies configured. And `proxyEnabled` was never true anyway:
   * nothing in the UI dispatched `toggleProxy`, so the flag was permanently false.
   *
   * What was live was the advice. Five error messages told the user to "enable the CORS proxy in
   * Advanced Options", a control that does not exist, so the one instruction the app gave for
   * recovering from a cross-origin failure was impossible to follow.
   *
   * Removed rather than implemented: routing capture traffic through a third-party CORS proxy
   * would send the page being captured to an untrusted host, and the capture already runs on the
   * backend. The empty proxy list was the safe configuration; this makes it the only one.
   */
  private async fetchWithProxy(url: string, options: RequestInit = {}): Promise<Response> {
    const controller = new AbortController();
    this.pendingAbortControllers.push(controller);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      if (response.ok) return response;
      throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
    } finally {
      this.pendingAbortControllers = this.pendingAbortControllers.filter((c) => c !== controller);
    }
  }

  // Improved website capture method with proper validation and error handling
  // Website capture - delegates to capture service
  async captureWebsite(
    request: CaptureWebsiteRequest,
    params: { figmaUID: string; figmaUserName: string; membership: string },
    progressCallback?: (status: string, progress: number) => void,
  ): Promise<{
    url: string;
    jsonData: any;
    /**
   * NEVER SENT by the worker. No route puts it in a response — `getUpdatedQuota` is imported by
   * two routes and called by none, and its free-tier limit defaults to 999999 regardless. Typed
   * optional so callers are forced to handle its absence, which is the only case that occurs.
   */
    /**
     * Present only for a multi-URL request, one entry per URL. Each carries that page's own
     * capture, which is what a caller must use: the pages cannot be concatenated, since the
     * renderer reads one message as one page seen at several viewports.
     */
    batchResults?: Array<{ url: string; success: boolean; jsonData?: any; error?: string }>;
  }> {
    const result = await captureService.captureWebsite(request, params, progressCallback);
    return {
      url: result.url || '',
      jsonData: result.jsonData,
      ...(result.batchResults ? { batchResults: result.batchResults } : {}),
    };
  }

  // Code capture - delegates to capture service
  async captureFromCode(
    request: CaptureFromCodeRequest,
    params: { figmaUID: string; figmaUserName: string; membership: string },
    progressCallback?: (status: string, progress: number) => void,
  ): Promise<{
    url: string;
    jsonData: any;
    /**
   * NEVER SENT by the worker. No route puts it in a response — `getUpdatedQuota` is imported by
   * two routes and called by none, and its free-tier limit defaults to 999999 regardless. Typed
   * optional so callers are forced to handle its absence, which is the only case that occurs.
   */
  }> {
    const result = await captureService.captureFromCode(request, params, progressCallback);
    return {
      url: result.url || '',
      jsonData: result.jsonData,
    };
  }

  // ZIP capture - delegates to capture service with enhanced progress tracking
  async captureFromZip(
    zipFile: File,
    request: CaptureFromZipRequest,
    params: { figmaUID: string; figmaUserName: string; membership: string },
    progressCallback?: (update: import('../services/CaptureService').ProgressUpdate) => void,
    cancellationToken?: import('../services/CaptureService').CancellationToken
  ): Promise<{
    url: string;
    jsonData: any;
    /**
   * NEVER SENT by the worker. No route puts it in a response — `getUpdatedQuota` is imported by
   * two routes and called by none, and its free-tier limit defaults to 999999 regardless. Typed
   * optional so callers are forced to handle its absence, which is the only case that occurs.
   */
    hostingSiteInfo?: { siteId: string };
  }> {
    const result = await captureService.captureFromZip(zipFile, request, params, progressCallback, cancellationToken);
    return {
      url: result.url || '',
      jsonData: result.jsonData,
      hostingSiteInfo: result.hostingSiteInfo,
    };
  }

  // Create cancellation token for operations
  createCancellationToken(): import('../services/CaptureService').CancellationToken {
    return captureService.createCancellationToken();
  }

  // Record import usage - delegates to user API service
  async recordImportUsage(figmaUID: string, figmaUserName: string = ""): Promise<void> {
    await userApiService.recordImportUsage(figmaUID, figmaUserName);
  }
}

// Export a singleton instance
export const api = new Api();
